import { useMemo, useState } from "react";
import { Box, Button, Pagination, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { colors } from "../../../theme/palette";
import { City, GoEvent } from "../../../types";
import { useProfileData } from "../../../hooks/useProfileData";
import { TrendingProfessionalsIcon, TrendingTalentsIcon } from "../../../assets/icons/MUI/NetworkIcons";
import { CreateEventIcon } from "../../../assets/icons/MUI/EventsIcons";
import Trending from "../../../shared/Trending";
import { useTrendingProfessionals, useTrendingTalents } from "../../../api/queries/trending/trendingQueries";
import { useEventsListing } from "../../../api/queries/events/eventsListQueries";
import APP_ROUTES from "../../../routers/routeConstants";

import EventsListTabs from "./EventsListTabs";
import EventsListFilter from "./EventsListFilter";
import EventsListCalendar from "./EventsListCalendar";
import EventCard, { EventCardLoading } from "./EventCard";

type Country = {
  cities: City[];
  code: string;
  id: number;
  name: string;
  nationality: string;
  shortCode: string;
  url: string;
  xCoord: number;
  yCoord: number;
};

const EventsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(0);
  const [tabValue, setTabValue] = useState<number>(0);
  const [calendarTab, setCalendarTab] = useState<string>("");
  const [minDate, setMinDate] = useState<string>("");
  const [maxDate, setMaxDate] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [country, setCountry] = useState<Country | null>(null);
  const [city, setCity] = useState<City | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);

  const hasActiveFilters = useMemo(() => {
    return !!city || !!status || !!type || !!country;
  }, [city, status, type, country]);

  const clearFilters = () => {
    setCountry(null);
    setStatus(null);
    setStatus(null);
    setType(null);
  };

  const handleChangeCalendarValue = (newValue: string) => {
    setCalendarTab(newValue);
    setPage(0);
  };

  const { trendingTalents, isTrendingTalentsLoading } = useTrendingTalents();
  const { trendingProfessionals, isTrendingProfessionalsLoading } = useTrendingProfessionals();

  const { data: userData } = useProfileData();
  const userId = userData.id;

  const { eventsListing, paginationData, isEventsListingResultLoading } = useEventsListing({
    page,
    tabValue,
    country,
    city,
    status,
    type,
    minDate,
    maxDate,
  });

  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "15px",
      }}
    >
      <Box
        sx={{
          padding: "12px",
          background: colors.White,
          borderRadius: "8px",
          border: "1px solid #EAECF0",
          width: "555px",
          overflow: "hidden",
          marginBottom: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Typography variant="h4ExtraBold">{t("pages.events.eventList.events")}</Typography>
          <Button
            variant="primary-sm"
            startIcon={<CreateEventIcon />}
            onClick={() => navigate(APP_ROUTES.CREATE_EVENT)}
          >
            {t("pages.events.eventList.createEvent")}
          </Button>
        </Box>
        <EventsListTabs tabValue={tabValue} handleChangeTabValue={handleChangeTabValue} />
        <EventsListFilter
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          country={country}
          setCountry={setCountry}
          city={city}
          setCity={setCity}
          status={status}
          setStatus={setStatus}
          type={type}
          setType={setType}
          clearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
        />
        <EventsListCalendar
          calendarTab={calendarTab}
          handleChangeCalendarValue={handleChangeCalendarValue}
          setMinDate={setMinDate}
          setMaxDate={setMaxDate}
          isLoading={isLoading}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "16px",
          }}
        >
          {eventsListing.length === 0 && !isEventsListingResultLoading && t("pages.events.eventList.noEvents")}
          {eventsListing.length > 0 &&
            !isEventsListingResultLoading &&
            eventsListing.map((event: GoEvent) => <EventCard key={event.id} event={event} userId={userId} />)}
          {isEventsListingResultLoading ? [...Array(3)].map(() => <EventCardLoading />) : <></>}
        </Box>
        {!isEventsListingResultLoading && eventsListing.length !== 0 ? (
          <Pagination
            count={paginationData?.totalPages}
            page={paginationData?.totalPages <= 0 ? 1 : page + 1}
            onChange={handleChangePage}
            sx={{
              margin: "33px auto 0",
              direction: "ltr !important",
              "& .MuiButtonBase-root": {
                borderRadius: "8px !important",
              },
              "& .MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
        ) : (
          <></>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Trending
          icon={<TrendingTalentsIcon />}
          label={t("components.trending.talents")}
          users={trendingTalents}
          isLoading={isTrendingTalentsLoading}
          query="talents"
          sum={true}
        />
        <Trending
          icon={<TrendingProfessionalsIcon />}
          label={t("components.trending.professionals")}
          users={trendingProfessionals}
          isLoading={isTrendingProfessionalsLoading}
          query="professionals"
        />
      </Box>
    </Box>
  );
};

export default EventsList;
